import { createContext, useState, useEffect, useContext } from "react";
import { useImmerReducer } from "use-immer";

import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { PERSON_ACTIONS } from "../Actions/PersonActions";
import { TICKET_ACTIONS } from "../Actions/TicketActions";
import { VEHICLE_ACTIONS } from "../Actions/VehicleActions";
import { localGetData, localStoreData, useLocalStorage } from "../utils/utils";
import { UserContext } from "./UserContext";
import { incidentReducerValidator } from "./IncidentsContextValidators";
import _ from "lodash";
import * as Sentry from "@sentry/react";

export const IncidentsContext = createContext();

const {
  OPEN_INCIDENT,
  CLOSE_INCIDENT,
  SET_INITIAL_CALL_INFO,
  UPDATE_INCIDENT,
  REPLACE_INCIDENT,
  UPDATE_OIS,
  UPDATE_OIS_FROM_UUID,
  REMOVE_OIS,
  ADD_EMPTY_OIS,
  UPDATE_PIS,
  UPDATE_VIS,
  UPDATE_VEHICLE,
  ADD_VIS,
  REMOVE_VIS,
  UPDATE_PERSON_IN_PIS,
  UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS,
  MERGE_PIS,
  UPDATE_PIS_FROM_UUID,
  REMOVE_PIS,
  ADD_PIS,
  ADD_OFFLINE_INCIDENT,
  EXTEND_PIS,
  ADD_OR_UPDATE_PIS,
  ADD_OR_UPDATE_VIS,
  UPDATE_INCIDENT_DETAIL,
  UPDATE_INCIDENT_DETAIL_NARRATIVE,
  REMOVE_INCIDENT_DETAIL_NARRATIVE,
  ADD_INCIDENT_DETAIL_NARRATIVE,
  ADD_OFFENSE,
  REMOVE_OFFENSE,
  UPDATE_OFFENSE,
  UPDATE_SVA,
  UPDATE_SVA_ALL_INCIDENTS,
  ADD_SVA,
  REMOVE_SVA,
  ADD_ARREST,
  REMOVE_ARREST,
  UPDATE_ARREST,
  ADD_CHARGE,
  REMOVE_CHARGE,
  UPDATE_CHARGE,
  UPDATE_CHARGE_ALL_INCIDENTS,
  UPDATE_PROPERTY,
  ADD_PROPERTY,
  REMOVE_PROPERTY,
  UPDATE_INCIDENT_TICKET,
  REMOVE_INCIDENT_TICKET,
  ADD_TICKET_TO_INCIDENT,
  UPDATE_CUSTODY_CHAIN,
  ADD_CUSTODY_CHAIN,
  REMOVE_CUSTODY_CHAIN,
  ADD_LAB_ANALYSIS,
  REMOVE_LAB_ANALYSIS,
  UPDATE_LAB_ANALYSIS,
  UPDATE_INCIDENT_REVIEW,
  ADD_NIC,
  REMOVE_NIC,
  UPDATE_NIC,
  ADD_INCIDENT_TO_LINK,
  REMOVE_INCIDENT_FROM_LINK,
  UPDATE_INCIDENT_ADDRESS_ALERT,
  UPDATE_QUERY_RETURNS,
  SET_NIC_ENTRY,
  UPDATE_OIS_MULTI_FIELD,
  SET_DISPATCH_NARRATIVES,
  // ADD_DISPATCH_NARRATIVE,
  // UPDATE_DISPATCH_NARRATIVE,
  REMOVE_DISPATCH_NARRATIVE,
  ADD_OR_UPDATE_DISPATCH_NARRATIVE,
} = INCIDENT_ACTIONS;

const {
  OPEN_PERSON,
  CLOSE_PERSON,
  SET_PERSON_INFO,
  UPDATE_PERSON_PERSONAL_ATTRIBUTE,
  MERGE_PERSON_PERSONAL_ATTRIBUTE,
  UPDATE_PERSON_ATTRIBUTE,
} = PERSON_ACTIONS;

const {
  OPEN_TICKET,
  CLOSE_TICKET,
  SET_TICKET_INFO,
  UPDATE_TICKET,
  // UPDATE_TICKET_PERSON,
} = TICKET_ACTIONS;

const { OPEN_VEHICLE, CLOSE_VEHICLE, SET_VEHICLE_INFO } = VEHICLE_ACTIONS;

function idArraysMatch(arr1 = [], arr2 = []) {
  const filtered1 = arr1.filter(id => id != null).map(String);
  const filtered2 = arr2.filter(id => id != null).map(String);
  return filtered1.some(id => filtered2.includes(id));
}

function incidentMatchesPayload(incident, payload) {
  return idArraysMatch(
    [incident?.id, incident?.uuid],
    [payload?.incidentId, payload?.incidentUuid],
  );
}

function pisMatchesPayload(pis, payload) {
  return idArraysMatch(
    [pis?.id, pis?.uuid],
    [payload?.pisId, payload?.pisUuid, payload?.pis?.id, payload?.pis?.uuid],
  );
}

function personMatchesPayload(person, payload) {
  return idArraysMatch(
    [person?.id, person?.uuid],
    [payload?.personId, payload?.personUuid],
  );
}

const handleValidationError = validation => {
  if (import.meta.env.DEV) {
    alert(validation.error);
  } else {
    Sentry.captureException(new Error(validation.error), {
      extra: validation.details,
    });
  }
  console.error(validation);
};

export const incidentReducer = (state, action) => {
  if (action.type === SET_INITIAL_CALL_INFO) {
    if (
      action.payload &&
      typeof action.payload === "object" &&
      !Array.isArray(action.payload) &&
      Object.keys(action.payload).length === 0
    ) {
      return state;
    }
  }

  const validation = incidentReducerValidator(action.type, action.payload);

  if (!validation.valid) {
    handleValidationError(validation);
    // return state;
    //continue on for now
  }

  switch (action.type) {
    case OPEN_INCIDENT: {
      let index = state.incidents.findIndex(i => i.id === action.payload);
      if (index > -1) {
        return state;
      } else {
        return {
          incidents: [...state.incidents, { id: action.payload, data: null }],
        };
      }
    }
    case CLOSE_INCIDENT:
      return {
        incidents: state.incidents.filter(i => i.id !== action.payload),
      };

    case SET_INITIAL_CALL_INFO: {
      let setInitialCallInfoKey =
        action.payload &&
        typeof action.payload === "object" &&
        !Array.isArray(action.payload)
          ? Object.keys(action.payload)[0]
          : null;
      let result = setInitialCallInfoKey
        ? action.payload[setInitialCallInfoKey]
        : null;
      if (!result?.id) {
        return state;
      }
      let setCallInfoIndex = state.incidents.findIndex(i => i.id === result.id);
      if (setCallInfoIndex > -1) {
        return void {
          incidents: (state.incidents[setCallInfoIndex] = _.merge(
            state.incidents[setCallInfoIndex],
            result,
          )),
          // incidents: state.incidents[setCallInfoIndex] = { ...state.incidents[setCallInfoIndex], ...result }
        };
      } else {
        return void {
          incidents: state.incidents.push({ ...result }),
        };
      }
    }

    case SET_NIC_ENTRY: {
      let incident = action.payload.value[Object.keys(action.payload.value)[0]];
      if (action.payload.incidentIndex > -1) {
        return void {
          incidents: (state.incidents[action.payload.incidentIndex] = {
            ...state.incidents[action.payload.incidentIndex],
            ...incident,
          }),
        };
      } else {
        alert("SET_NIC_ENTRY reducer cannot find the incident index");
        break;
      }
    }

    case ADD_OFFLINE_INCIDENT:
      // return { incidents: [...state.incidents, { id: action.payload.uuid, uuid: action.payload.uuid, callTime: action.payload.callTime, incidentNumber: action.payload.incidentNumber, personIncidentSpecifics: action.payload.personIncidentSpecifics, officerIncidentSpecifics: action.payload.officerIncidentSpecifics, address: action.payload.address }] };
      return { incidents: [...state.incidents, action.payload] };

    case UPDATE_INCIDENT: {
      let UIP_IncidentIndex =
        action.payload?.incidentIndex >= 0
          ? action.payload.incidentIndex
          : state.incidents.findIndex(
              incident =>
                incident.id?.toString() === action.payload.id?.toString() ||
                incident.uuid?.toString() === action.payload.id?.toString(),
            );
      if (UIP_IncidentIndex > -1) {
        if (action.payload.accessor) {
          if (Array.isArray(action.payload.value)) {
            return void {
              incidents: (state.incidents[UIP_IncidentIndex][
                action.payload.accessor
              ][action.payload.property] = action.payload.value),
            };
          } else if (
            typeof action.payload.value === "object" &&
            action.payload.value !== null
          ) {
            return void {
              incidents: (state.incidents[UIP_IncidentIndex][
                action.payload.accessor
              ][action.payload.property] = {
                ...state.incidents[UIP_IncidentIndex][action.payload.accessor][
                  action.payload.property
                ],
                ...action.payload.value,
              }),
            };
          } else {
            return void {
              incidents: (state.incidents[UIP_IncidentIndex][
                action.payload.accessor
              ][action.payload.property] = action.payload.value),
            };
          }
        } else {
          if (
            !!state.incidents[UIP_IncidentIndex][action.payload.property] &&
            typeof state.incidents[UIP_IncidentIndex][
              action.payload.property
            ] === "object" &&
            !Array.isArray(
              state.incidents[UIP_IncidentIndex][action.payload.property],
            )
          ) {
            return void {
              // incidents: state.incidents[UIP_IncidentIndex][action.payload.property] = _.merge(state.incidents[UIP_IncidentIndex][action.payload.property], action.payload.value)
              incidents: (state.incidents[UIP_IncidentIndex][
                action.payload.property
              ] = _.merge(
                state.incidents[UIP_IncidentIndex][action.payload.property],
                action.payload.value,
              )),
            };
          } else {
            return void {
              incidents: (state.incidents[UIP_IncidentIndex][
                action.payload.property
              ] = action.payload.value),
            };
          }
        }
      } else {
        break;
      }
    }

    case UPDATE_INCIDENT_ADDRESS_ALERT:
      return void {
        incidents: state.incidents.map(i => {
          if (
            i.address.id === action.payload.addressId ||
            i.address.uuid === action.payload.addressId
          ) {
            i.address.validAddressAlerts.map(vaa => {
              if (
                vaa.id === action.payload.addressAlertId ||
                vaa.uuid === action.payload.addressAlertId
              ) {
                vaa[action.payload.property] = action.payload.value;
              }
              return vaa;
            });
          }
          return i;
        }),
      };

    case UPDATE_INCIDENT_DETAIL:
      if (action.payload.incidentIndex >= 0) {
        if (action.payload.property) {
          return void {
            incidents: (state.incidents[
              action.payload.incidentIndex
            ].incidentDetail[action.payload.property] = action.payload.value),
          };
        } else {
          return void {
            incidents: (state.incidents[
              action.payload.incidentIndex
            ].incidentDetail = {
              ...state.incidents[action.payload.incidentIndex].incidentDetail,
              ...action.payload.value,
            }),
          };
        }
      } else {
        if (action.payload.property) {
          return void {
            incidents: state.incidents.map(i => {
              if (i.id === action.payload.incidentId) {
                i.incidentDetail = i.incidentDetail[action.payload.property] =
                  action.payload.value;
                return i;
              } else {
                return i;
              }
            }),
          };
        } else {
          return void {
            incidents: state.incidents.map(i => {
              if (i.id === action.payload.incidentId) {
                i.incidentDetail = i.incidentDetail = {
                  ...i.incidentDetail,
                  ...action.payload.value,
                };
                return i;
              } else {
                return i;
              }
            }),
          };
        }
      }

    case REMOVE_INCIDENT_DETAIL_NARRATIVE:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.narrativeIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentDetail.incidentNarratives.splice(
            action.payload.narrativeIndex,
            1,
          ),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              i.incidentDetail.incidentNarratives =
                i.incidentDetail.incidentNarratives.filter(
                  narrative =>
                    (!!narrative.id &&
                      narrative.id !== action.payload.narrativeId) ||
                    (!!narrative.uuid &&
                      narrative.uuid !== action.payload.uuid),
                );
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case ADD_INCIDENT_DETAIL_NARRATIVE:
      if (action.payload.incidentIndex !== undefined) {
        if (
          !Object.prototype.hasOwnProperty.call(
            state.incidents[action.payload.incidentIndex].incidentDetail,
            "incidentNarratives",
          )
        ) {
          state.incidents[
            action.payload.incidentIndex
          ].incidentDetail.incidentNarratives = [];
        }
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentDetail.incidentNarratives.push(action.payload.narrative),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let newIncident = Object.assign({}, i);
              if (
                !Object.prototype.hasOwnProperty.call(
                  newIncident.incidentDetail,
                  "incidentNarratives",
                )
              ) {
                newIncident.incidentDetail.incidentNarratives = [];
              }
              newIncident.incidentDetail.incidentNarratives.push(
                action.payload.narrative,
              );
              return newIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_INCIDENT_DETAIL_NARRATIVE:
      if (
        action.payload.incidentIndex >= 0 &&
        action.payload.narrativeIndex >= 0
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentDetail.incidentNarratives[action.payload.narrativeIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i.incidentDetail?.incidentNarratives?.map(n => {
                if (
                  n.id === action.payload.narrativeId ||
                  n.uuid === action.payload.narrativeId
                ) {
                  n[action.payload.property] = action.payload.value;
                }
                return n;
              });
            }
            return i;
          }),
        };
      }

    case SET_DISPATCH_NARRATIVES:
      return {
        ...state,
        incidents: state.incidents.map(incident =>
          incident.id?.toString() === action.payload.incidentId?.toString() ||
          incident.uuid?.toString() === action.payload.incidentId?.toString()
            ? {
                ...incident,
                narrativeSegments: action.payload.segments,
              }
            : incident,
        ),
      };

    case ADD_OR_UPDATE_DISPATCH_NARRATIVE:
      return {
        ...state,
        incidents: state.incidents.map(incident =>
          incident.id?.toString() === action.payload.incidentId?.toString() ||
          incident.uuid?.toString() === action.payload.incidentId?.toString()
            ? {
                ...incident,
                narrativeSegments: (() => {
                  const segments = incident.narrativeSegments || [];
                  const idx = segments.findIndex(
                    segment =>
                      segment.id?.toString() ===
                        action.payload.segment.id?.toString() ||
                      segment.uuid?.toString() ===
                        action.payload.segment.id?.toString() ||
                      segment.id?.toString() ===
                        action.payload.segment.uuid?.toString(),
                  );
                  if (idx > -1) {
                    // Update existing segment
                    return segments.map((segment, i) =>
                      i === idx
                        ? { ...segment, ...action.payload.segment }
                        : segment,
                    );
                  } else {
                    // Add new segment to front
                    return [action.payload.segment, ...segments];
                  }
                })(),
              }
            : incident,
        ),
      };

    case REMOVE_DISPATCH_NARRATIVE:
      return {
        ...state,
        incidents: state.incidents.map(incident =>
          incident.id?.toString() === action.payload.incidentId?.toString() ||
          incident.uuid?.toString() === action.payload.incidentId?.toString()
            ? {
                ...incident,
                narrativeSegments: incident.narrativeSegments.filter(
                  segment =>
                    segment.id?.toString() !==
                    action.payload.segmentId?.toString(),
                ),
              }
            : incident,
        ),
      };

    case REPLACE_INCIDENT:
      return {
        incidents: state.incidents.map(i => {
          if (
            i.id === action.payload.id ||
            i.uuid === action.payload.uuid ||
            i.uuid === action.payload.id
          ) {
            let openIncidentsArr =
              localGetData(`${state.userId}-open-incidents`) || [];
            let closeIndex = openIncidentsArr.findIndex(
              i => i === action.payload.uuid,
            );
            if (closeIndex > -1) {
              openIncidentsArr.splice(closeIndex, 1);
            }
            openIncidentsArr.push(action.payload.id);
            localStoreData(openIncidentsArr, `${state.userId}-open-incidents`);

            return {
              ...i,
              id: action.payload.id,
              uuid: action.payload.uuid,
              incidentNumber: action.payload.incidentNumber,
            };
          } else {
            return i;
          }
        }),
      };

    case ADD_INCIDENT_TO_LINK:
      if (action.payload.incidentIndex !== undefined) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].linkedIncidents.push(action.payload.linkedIncident),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let updatedIncident = Object.assign({}, i);
              updatedIncident.linkedIncidents.push(
                action.payload.linkedIncident,
              );
              return updatedIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case REMOVE_INCIDENT_FROM_LINK:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.linkedIncidents = i.linkedIncidents.filter(
              li => li.id !== action.payload.linkedIncidentId,
            );
            return i;
          } else {
            return i;
          }
        }),
      };

    case ADD_EMPTY_OIS:
      if (action.payload.incidentIndex >= 0) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].officerIncidentSpecifics.push(action.payload.ois),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let updatedIncident = Object.assign({}, i);
              updatedIncident.officerIncidentSpecifics.push(action.payload.ois);
              return updatedIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_OIS_MULTI_FIELD: {
      const MF_incidentIndex = state.incidents.findIndex(
        incident =>
          incident.id?.toString() === action.payload.incidentId?.toString() ||
          incident.uuid?.toString() === action.payload.incidentId?.toString(),
      );
      const MF_oisIndex = state.incidents[
        MF_incidentIndex
      ]?.officerIncidentSpecifics.findIndex(
        ois =>
          ois.id?.toString() === action.payload.oisId?.toString() ||
          ois.id?.toString() === action.payload.oisUuid?.toString(),
      );
      if (MF_incidentIndex > -1) {
        if (MF_oisIndex > -1) {
          return void {
            incidents: (state.incidents[
              MF_incidentIndex
            ].officerIncidentSpecifics[MF_oisIndex] = {
              ...state.incidents[MF_incidentIndex].officerIncidentSpecifics[
                MF_oisIndex
              ],
              ...action.payload.updatedOis,
            }),
          };
        } else {
          return void {
            incidents: state.incidents[
              MF_incidentIndex
            ].officerIncidentSpecifics.push(action.payload.updatedOis),
          };
        }
      } else {
        return;
      }
    }

    case UPDATE_OIS:
      if (
        action.payload.incidentIndex >= 0 &&
        action.payload.oisIndex >= 0 &&
        action.payload.property
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].officerIncidentSpecifics[action.payload.oisIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              i.officerIncidentSpecifics.map(ois => {
                if (
                  (!!ois?.id && ois?.id === action.payload.oisId) ||
                  (!!ois?.uuid && ois?.uuid === action.payload.uuid)
                ) {
                  ois[action.payload.property] = action.payload.value;
                }
                return ois;
              });
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_OIS_FROM_UUID:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.officerIncidentSpecifics.map(ois => {
              if (ois?.uuid === action.payload.oisUuid) {
                ois.id = action.payload.ois.id;
                ois.__typename = action.payload.ois.__typename;
              }
              return ois;
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case MERGE_PIS:
      //find the personIncidentSpecifics by ID in the incidents then merge the action.payload.value with the original object
      //return the state with the updated personIncidentSpecifics
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.personIncidentSpecifics.map(pis => {
              if (pis.id === action.payload.value.id) {
                const mergedResult = _.merge({}, pis, action.payload.value);
                return mergedResult;
              } else {
                return pis;
              }
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case UPDATE_PIS:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload?.incidentId])) {
            i.personIncidentSpecifics.map(pis => {
              if (
                idArraysMatch(
                  [pis?.id, pis?.uuid],
                  [action.payload.pisId, action.payload.pisUuid],
                )
              ) {
                pis[action.payload.property] = action.payload.value;
              }
              return pis;
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case ADD_VIS:
      if (action.payload.incidentIndex !== undefined) {
        const addVisIndex = state.incidents[
          action.payload.incidentIndex
        ].vehicleIncidentSpecifics?.findIndex(
          vis => vis.id === action.payload.vis.id,
        );
        if (addVisIndex > -1) {
          return void {
            incidents: (state.incidents[
              action.payload.incidentIndex
            ].vehicleIncidentSpecifics[addVisIndex] = {
              ...state.incidents[action.payload.incidentIndex]
                .vehicleIncidentSpecifics[addVisIndex],
              ...action.payload.vis,
            }),
          };
        } else {
          return void {
            incidents: state.incidents[
              action.payload.incidentIndex
            ].vehicleIncidentSpecifics.push(action.payload.vis),
            //  = state.incidents[action.payload.incidentIndex].vehicleIncidentSpecifics.push(action.payload.vis)
          };
        }
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let newIncident = Object.assign({}, i);
              const addVisIndexInIncident =
                newIncident.vehicleIncidentSpecifics.findIndex(
                  vis => vis.id === action.payload.vis.id,
                );
              if (addVisIndexInIncident > -1) {
                newIncident.vehicleIncidentSpecifics[addVisIndexInIncident] = {
                  ...newIncident.vehicleIncidentSpecifics[
                    addVisIndexInIncident
                  ],
                  ...action.payload.vis,
                };
              } else {
                newIncident.vehicleIncidentSpecifics.push(action.payload.vis);
              }
              return newIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_VIS:
      if (action.payload.visIndex !== undefined) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].vehicleIncidentSpecifics[action.payload.visIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else if (action.payload.incidentId !== undefined) {
        return void {
          incidents: state.incidents.map(i => {
            if (
              idArraysMatch(
                [i?.id, i?.uuid],
                [action.payload.incidentId, action.payload.incidentUuid],
              )
            ) {
              i.vehicleIncidentSpecifics = i.vehicleIncidentSpecifics.map(
                vis => {
                  if (
                    idArraysMatch(
                      [vis?.id, vis?.uuid],
                      [action.payload.visUuid, action.payload.visId],
                    )
                  ) {
                    vis[action.payload.property] = action.payload.value;
                  }
                  return vis;
                },
              );
            }
            return i;
          }),
        };
      } else {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].vehicleIncidentSpecifics.map(vis => {
            if (
              idArraysMatch(
                [vis?.id, vis?.uuid],
                [action.payload.visUuid, action.payload.visId],
              )
            ) {
              vis[action.payload.property] = action.payload.value;
            }
            return vis;
          }),
        };
      }
    case UPDATE_VEHICLE:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.visIndex !== undefined
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].vehicleIncidentSpecifics[action.payload.visIndex].vehicle[
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            i?.vehicleIncidentSpecifics?.map(vis => {
              if (
                idArraysMatch(
                  [vis?.vehicle?.id, vis?.vehicle?.uuid],
                  [action.payload?.vehicleUuid, action.payload?.vehicleId],
                )
              ) {
                vis.vehicle[action.payload.property] = action.payload.value;
              }
              return vis;
            });
            return i;
          }),
        };
      }

    case EXTEND_PIS:
      return void state.incidents.forEach(incident => {
        if (!incidentMatchesPayload(incident, action.payload)) return;

        const pis = incident.personIncidentSpecifics?.find(personSpecific =>
          pisMatchesPayload(personSpecific, action.payload),
        );

        if (pis) {
          Object.assign(pis, action.payload.pis);
        }
      });

    case UPDATE_PIS_FROM_UUID:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.personIncidentSpecifics.map(pis => {
              if (pis?.uuid === action.payload.pisUuid) {
                pis.id = action.payload.pis.id;
                pis.bac = action.payload.pis.bac;
                pis.__typename = action.payload.pis.__typename;
              }
              return pis;
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case ADD_PIS:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            let newIncident = Object.assign({}, i);
            newIncident.personIncidentSpecifics.push(action.payload.pis);
            return newIncident;
          } else {
            return i;
          }
        }),
      };

    case ADD_OR_UPDATE_PIS:
      return void state.incidents.forEach(incident => {
        if (!incidentMatchesPayload(incident, action.payload)) return;
        if (!Array.isArray(incident.personIncidentSpecifics)) return;

        const pisIndex = incident.personIncidentSpecifics.findIndex(pis =>
          pisMatchesPayload(pis, action.payload),
        );

        if (pisIndex >= 0) {
          incident.personIncidentSpecifics[pisIndex] = action.payload.pis;
        } else {
          incident.personIncidentSpecifics.push(action.payload.pis);
        }
      });

    case ADD_OR_UPDATE_VIS: {
      const aorup_vis_index = state.incidents[
        action.payload.incidentIndex
      ]?.vehicleIncidentSpecifics.findIndex(
        vis =>
          vis?.id === action.payload?.vis?.id ||
          vis?.id === action.payload.visUuid,
      );
      if (aorup_vis_index >= 0) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].vehicleIncidentSpecifics[aorup_vis_index] = action.payload.vis),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let newIncident = Object.assign({}, i);
              newIncident.vehicleIncidentSpecifics.push(action.payload.vis);
              return newIncident;
            } else {
              return i;
            }
          }),
        };
      }
    }

    case UPDATE_PERSON_IN_PIS:
      return void {
        incidents: state.incidents.map(i => {
          if (
            idArraysMatch(
              [i.id, i?.uuid],
              [action.payload?.incidentId, action.payload?.incidentUuid],
            )
          ) {
            i.personIncidentSpecifics.map(pis => {
              if (
                idArraysMatch(
                  [pis?.id, pis?.uuid],
                  [action.payload?.pisId, action.payload?.pisUuid],
                )
              ) {
                if (action.payload.accessor) {
                  pis.person[action.payload.accessor][action.payload.property] =
                    action.payload.value;
                } else {
                  pis.person[action.payload.property] = action.payload.value;
                }
              }
              return pis;
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case UPDATE_PERSON_PERSONAL_ATTRIBUTE_IN_PIS:
      return void state.incidents.forEach(incident => {
        incident.personIncidentSpecifics?.forEach(pis => {
          if (!personMatchesPayload(pis?.person, action.payload)) return;
          if (!pis.person?.personalAttribute) return;

          pis.person.personalAttribute[action.payload.property] =
            action.payload.value;
        });
      });

    case REMOVE_PIS:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.personIncidentSpecifics = i.personIncidentSpecifics.filter(
              ois => ois.id !== action.payload.pisId,
            );
            return i;
          } else {
            return i;
          }
        }),
      };

    case REMOVE_OIS:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.officerIncidentSpecifics =
              i.officerIncidentSpecifics?.filter(
                ois =>
                  (!!ois.id && ois.id !== action.payload.oisId) ||
                  (!!ois.uuid && ois.uuid !== action.payload.uuid),
              ) || [];
            return i;
          } else {
            return i;
          }
        }),
      };

    case REMOVE_VIS:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.visIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].vehicleIncidentSpecifics.splice(action.payload.visIndex, 1),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (i.id === action.payload.incidentId) {
              i.vehicleIncidentSpecifics = i.vehicleIncidentSpecifics.filter(
                vis =>
                  (!!vis.id && vis.id !== action.payload.visId) ||
                  (!!vis.uuid && vis.uuid !== action.payload.uuid),
              );
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case ADD_OFFENSE:
      return void {
        incidents: state.incidents.map(i => {
          if (i.id === action.payload.incidentId) {
            let newIncident = Object.assign({}, i);
            newIncident.incidentDetail.incidentOffenses.push(
              action.payload.offense,
            );
            return newIncident;
          } else {
            return i;
          }
        }),
      };

    case REMOVE_OFFENSE:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
            i.incidentDetail.incidentOffenses =
              i.incidentDetail.incidentOffenses.filter(
                offense =>
                  (!!offense.id && offense.id !== action.payload.offenseId) ||
                  (!!offense.uuid && offense.uuid !== action.payload.uuid),
              );
            return i;
          } else {
            return i;
          }
        }),
      };

    case UPDATE_OFFENSE:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload?.incidentId])) {
            i?.incidentDetail?.incidentOffenses.map(offense => {
              if (
                idArraysMatch(
                  [offense?.id, offense?.uuid],
                  [action.payload?.offenseId, action.payload?.uuid],
                )
              ) {
                offense[action.payload?.property] = action.payload?.value;
              }
              return offense;
            });
          }
          return i;
        }),
      };

    case UPDATE_SVA:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload?.incidentId])) {
            i?.incidentDetail?.incidentOffenses.map(offense => {
              if (
                idArraysMatch(
                  [offense?.id, offense?.uuid],
                  [action.payload?.offenseId],
                )
              ) {
                offense.suspectVictimAffiliations.map(sva => {
                  if (
                    sva.id === action.payload?.svaId ||
                    sva.uuid === action.payload?.svaId
                  ) {
                    sva[action.payload?.property] = action.payload?.value;
                  }
                  return sva;
                });
              }
              return offense;
            });
          }
          return i;
        }),
      };

    case UPDATE_SVA_ALL_INCIDENTS:
      return void {
        incidents: state.incidents.map(i => {
          i?.incidentDetail?.incidentOffenses?.map(offense => {
            // match the target offense (id or uuid)
            if (
              idArraysMatch(
                [offense?.id, offense?.uuid],
                [action.payload?.offenseId, action.payload?.offenseUuid],
              )
            ) {
              // update the matching SVA within that offense
              offense.suspectVictimAffiliations?.map(sva => {
                if (
                  idArraysMatch(
                    [sva?.id, sva?.uuid],
                    [action.payload?.svaId, action.payload?.svaUuid],
                  )
                ) {
                  sva[action.payload?.property] = action.payload?.value;
                }
                return sva;
              });
            }
            return offense;
          });
          return i;
        }),
      };

    case REMOVE_SVA:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
            i?.incidentDetail?.incidentOffenses?.forEach(offense => {
              if (
                idArraysMatch(
                  [offense?.id, offense?.uuid],
                  [action.payload.offenseId],
                )
              ) {
                offense.suspectVictimAffiliations =
                  offense.suspectVictimAffiliations?.filter(
                    sva =>
                      !idArraysMatch(
                        [sva.id, sva.uuid],
                        [action.payload?.svaId, action.payload?.svaUuid],
                      ),
                  );
              }
            });
          }
          return i;
        }),
      };

    // case ADD_SVA:
    //   return void {
    //     incidents: state.incidents.map(i => {
    //       if (i.id === action.payload.incidentId) {
    //         let newIncident = Object.assign({}, i);
    //         newIncident.incidentDetail.incidentOffenses.push(
    //           action.payload.sva
    //         );
    //         return newIncident;
    //       } else {
    //         return i;
    //       }
    //     }),
    //   };
    case ADD_SVA:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i.id, i.uuid], [action.payload.incidentId])) {
            let newIncident = { ...i };
            newIncident.incidentDetail.incidentOffenses =
              newIncident.incidentDetail.incidentOffenses.map(offense => {
                if (
                  idArraysMatch(
                    [offense.id, offense.uuid],
                    [action.payload.offenseId],
                  )
                ) {
                  const affiliations = Array.isArray(
                    offense.suspectVictimAffiliations,
                  )
                    ? offense.suspectVictimAffiliations
                    : [];
                  return {
                    ...offense,
                    suspectVictimAffiliations: [
                      ...affiliations,
                      action.payload.sva,
                    ],
                  };
                }
                return offense;
              });
            return newIncident;
          }
          return i;
        }),
      };

    case ADD_ARREST:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
            let newIncident = Object.assign({}, i);
            newIncident.incidentArrests.push(action.payload.arrest);
            return newIncident;
          } else {
            return i;
          }
        }),
      };

    case REMOVE_ARREST:
      return void {
        incidents: state.incidents.map(i => {
          i.incidentArrests = i.incidentArrests.filter(
            arrest =>
              !idArraysMatch(
                [arrest?.id, arrest?.uuid],
                [action.payload?.arrestId, action.payload?.arrestUuid],
              ),
          );
          return i;
        }),
      };

    case UPDATE_ARREST:
      return void {
        incidents: state.incidents.map(i => {
          i.incidentArrests?.map(arrest => {
            if (
              idArraysMatch(
                [arrest?.id, arrest?.uuid],
                [action.payload?.arrestId],
              )
            ) {
              arrest[action.payload.property] = action.payload.value;
            }
            return arrest;
          });
          return i;
        }),
      };

    // case ADD_CHARGE:
    //   return void {
    //     incidents: state.incidents.map(i => {
    //       if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
    //         let newIncident = { ...i };
    //         newIncident.incidentArrests = newIncident.incidentArrests.map(
    //           arrest => {
    //             if (
    //               idArraysMatch(
    //                 [arrest?.id, arrest?.uuid],
    //                 [action.payload.arrestId]
    //               )
    //             ) {
    //               const charges = Array.isArray(arrest.arrestCharges)
    //                 ? arrest.arrestCharges
    //                 : [];
    //               return {
    //                 ...arrest,
    //                 arrestCharges: [...charges, action.payload.charge],
    //               };
    //             }
    //             return arrest;
    //           }
    //         );
    //         return newIncident;
    //       }
    //       return i;
    //     }),
    //   };

    case ADD_CHARGE:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
            i.incidentArrests.forEach(arrest => {
              if (
                idArraysMatch(
                  [arrest?.id, arrest?.uuid],
                  [action.payload.arrestId],
                )
              ) {
                if (!Array.isArray(arrest.arrestCharges)) {
                  arrest.arrestCharges = [];
                }
                arrest.arrestCharges.push(action.payload.charge);
                return arrest;
              }
            });
            return i;
          }
          return i;
        }),
      };

    case REMOVE_CHARGE:
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload?.incidentId])) {
            i.incidentArrests = i.incidentArrests.map(arrest => {
              if (
                idArraysMatch(
                  [arrest?.id, arrest?.uuid],
                  [action.payload?.arrestId],
                )
              ) {
                arrest.arrestCharges = arrest.arrestCharges.filter(
                  charge =>
                    !idArraysMatch(
                      [action.payload?.chargeId, action.payload?.chargeUuid],
                      [charge?.id, charge?.uuid],
                    ),
                );
              }
              return arrest;
            });
          }
          return i;
        }),
      };

    case UPDATE_CHARGE:
      if (!action.payload.property) break;
      return void {
        incidents: state.incidents.map(i => {
          if (idArraysMatch([i?.id, i?.uuid], [action.payload.incidentId])) {
            i.incidentArrests = i.incidentArrests.map(arrest => {
              if (
                idArraysMatch(
                  [arrest?.id, arrest?.uuid],
                  [action.payload.arrestId],
                )
              ) {
                arrest.arrestCharges = arrest.arrestCharges.map(charge => {
                  if (
                    idArraysMatch(
                      [charge?.id, charge?.uuid],
                      [action.payload.chargeId],
                    )
                  ) {
                    return {
                      ...charge,
                      [action.payload.property]: action.payload.value,
                    };
                  }
                  return charge;
                });
              }
              return arrest;
            });
          }
          return i;
        }),
      };
    case UPDATE_CHARGE_ALL_INCIDENTS: {
      if (!("property" in (action?.payload ?? {}))) break;

      return void {
        incidents: state.incidents.map(i => {
          if (!Array.isArray(i.incidentArrests)) return i;

          i.incidentArrests = i.incidentArrests.map(arrest => {
            if (
              idArraysMatch(
                [arrest?.id, arrest?.uuid],
                [action.payload?.arrestId, action.payload?.arrestUuid],
              )
            ) {
              if (Array.isArray(arrest.arrestCharges)) {
                arrest.arrestCharges = arrest.arrestCharges.map(charge => {
                  if (
                    idArraysMatch(
                      [charge?.id, charge?.uuid],
                      [action.payload?.chargeId, action.payload?.chargeUuid],
                    )
                  ) {
                    return {
                      ...charge,
                      [action.payload.property]: action.payload.value,
                    };
                  }
                  return charge;
                });
              }
            }
            return arrest;
          });

          return i;
        }),
      };
    }

    case ADD_PROPERTY:
      if (action.payload.incidentIndex !== undefined) {
        if (state.incidents[action.payload.incidentIndex]?.incidentProperties) {
          return void {
            incidents: state.incidents[
              action.payload.incidentIndex
            ].incidentProperties.push(action.payload.incidentProperty),
          };
        } else {
          return void {
            incidents: (state.incidents[
              action.payload.incidentIndex
            ].incidentProperties = [action.payload.incidentProperty]),
          };
        }
      } else {
        // Update when the mutation exists
        // return void {
        //   incidents: state.incidents.map(i => {
        //     if (i.id === action.payload.incidentId) {
        //       let newIncident = Object.assign({}, i)
        //       newIncident.incidentArrests.push(action.payload.arrest)
        //       return newIncident;
        //     } else {
        //       return i;
        //     }
        //   })
        // }
        alert("Finish reducer");
        break;
      }

    case REMOVE_PROPERTY:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.incidentPropertyIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentProperties.splice(action.payload.incidentPropertyIndex, 1),
        };
      } else {
        // return void {
        //   incidents: state.incidents.map(i => {
        //     i.incidentProperties = i.incidentProperties.find(ip => ip.id === action.payload.incidentPropertyId).filter(property => (!!property.id && property.id !== action.payload.incidentPropertyId) || (!!property.uuid && property.uuid !== action.payload.incidentPropertyId))
        //     return i
        //   })
        // }
        alert("needs to be completed");
        break;
      }

    case UPDATE_PROPERTY:
      if (
        !action.payload.property &&
        action.payload.propertyIndex >= 0 &&
        action.payload.incidentIndex >= 0
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[action.payload.propertyIndex] = {
            ...state.incidents[action.payload.incidentIndex].incidentProperties[
              action.payload.propertyIndex
            ],
            ...action.payload.value,
          }),
        };
      } else if (
        action.payload.property &&
        action.payload.propertyIndex >= 0 &&
        action.payload.incidentIndex >= 0
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[action.payload.propertyIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i.incidentProperties.map(incidentProperty => {
                if (
                  incidentProperty?.id === action.payload.incidentPropertyId
                ) {
                  incidentProperty[action.payload.property] =
                    action.payload.value;
                }
                return incidentProperty;
              });
              return i;
            }
            return i;
          }),
        };
      }

    case ADD_CUSTODY_CHAIN:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.incidentPropertyIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.incidentPropertyIndex
          ].custodyChains.push(action.payload.custodyChain),
        };
      } else {
        // Update when the mutation exists
        // return void {
        //   incidents: state.incidents.map(i => {
        //     if (i.id === action.payload.incidentId) {
        //       let newIncident = Object.assign({}, i)
        //       newIncident.incidentArrests.push(action.payload.arrest)
        //       return newIncident;
        //     } else {
        //       return i;
        //     }
        //   })
        // }
        alert("Update reducer");
        break;
      }

    case REMOVE_CUSTODY_CHAIN:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.incidentPropertyIndex !== undefined &&
        action.payload.custodyChainIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.incidentPropertyIndex
          ].custodyChains.splice(action.payload.custodyChainIndex, 1),
        };
      } else {
        // return void {
        //   incidents: state.incidents.map(i => {
        //     i.incidentProperties = i.incidentProperties.filter(property => (!!property.id && property.id !== action.payload.incidentPropertyId) || (!!property.uuid && property.uuid !== action.payload.incidentPropertyId))
        //     return i
        //   })
        // }
        alert("Update reducer");
        break;
      }

    case UPDATE_CUSTODY_CHAIN:
      if (
        action.payload.property &&
        action.payload.custodyChainIndex >= 0 &&
        action.payload.incidentPropertyIndex >= 0 &&
        action.payload.incidentIndex >= 0
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.incidentPropertyIndex
          ].custodyChains[action.payload.custodyChainIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            i.incidentProperties.map(incidentProperty => {
              if (
                incidentProperty.id === action.payload.incidentPropertyId ||
                incidentProperty.uuid === action.payload.incidentPropertyId
              ) {
                incidentProperty.custodyChains.map(custodyChain => {
                  if (
                    custodyChain?.id === action.payload.custodyChainId ||
                    custodyChain?.uuid === action.payload.custodyChainId
                  ) {
                    custodyChain[action.payload.property] =
                      action.payload.value;
                  }
                  return custodyChain;
                });
              }
              return incidentProperty;
            });
            return i;
          }),
        };
      }

    case ADD_LAB_ANALYSIS:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.incidentPropertyIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.incidentPropertyIndex
          ].labAnalysisInstances.push(action.payload.labAnalysis),
        };
      } else {
        // Update when the mutation exists
        // return void {
        //   incidents: state.incidents.map(i => {
        //     if (i.id === action.payload.incidentId) {
        //       let newIncident = Object.assign({}, i)
        //       newIncident.incidentArrests.push(action.payload.arrest)
        //       return newIncident;
        //     } else {
        //       return i;
        //     }
        //   })
        // }
        alert("Update reducer");
        break;
      }

    case REMOVE_LAB_ANALYSIS:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.incidentPropertyIndex !== undefined &&
        action.payload.labAnalysisIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.incidentPropertyIndex
          ].labAnalysisInstances.splice(action.payload.labAnalysisIndex, 1),
        };
      } else {
        // return void {
        //   incidents: state.incidents.map(i => {
        //     i.incidentProperties = i.incidentProperties.filter(property => (!!property.id && property.id !== action.payload.incidentPropertyId) || (!!property.uuid && property.uuid !== action.payload.incidentPropertyId))
        //     return i
        //   })
        // }
        alert("Update reducer");
        break;
      }

    case UPDATE_LAB_ANALYSIS:
      if (
        action.payload.property &&
        action.payload.labAnalysisIndex >= 0 &&
        action.payload.propertyIndex >= 0 &&
        action.payload.incidentIndex >= 0
      ) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentProperties[
            action.payload.propertyIndex
          ].labAnalysisInstances[action.payload.labAnalysisIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            i.incidentProperties.map(incidentProperty => {
              if (
                incidentProperty.id === action.payload.incidentPropertyId ||
                incidentProperty.uuid === action.payload.incidentPropertyId
              ) {
                incidentProperty.labAnalysisInstances.map(labAnalysis => {
                  if (
                    labAnalysis?.id === action.payload.labAnalysisId ||
                    labAnalysis?.uuid === action.payload.labAnalysisId
                  ) {
                    labAnalysis[action.payload.property] = action.payload.value;
                  }
                  return labAnalysis;
                });
              }
              return incidentProperty;
            });
            return i;
          }),
        };
      }

    case UPDATE_INCIDENT_TICKET:
      return void {
        incidents: state.incidents.map(i => {
          if (
            idArraysMatch(
              [i?.id, i?.uuid],
              [action.payload.incidentId, action.payload.incidentUuid],
            )
          ) {
            i.incidentTickets.map(t => {
              if (
                idArraysMatch(
                  [t?.id, t?.uuid],
                  [action.payload.ticketId, action.payload.ticketUuid],
                )
              ) {
                if (action.payload.accessor) {
                  if (
                    Object.prototype.hasOwnProperty.call(
                      t[action.payload.accessor] || {},
                      action.payload.property,
                    )
                  ) {
                    t[action.payload.accessor][action.payload.property] =
                      action.payload.value;
                  } else {
                    t[action.payload.accessor] = {
                      ...(t[action.payload.accessor] || {}),
                      [action.payload.property]: action.payload.value,
                    };
                  }
                } else {
                  t[action.payload.property] = action.payload.value;
                }
              }
              return t;
            });
          }
          return i;
        }),
      };

    case REMOVE_INCIDENT_TICKET:
      return void {
        incidents: state.incidents.map(i => {
          if (
            idArraysMatch(
              [i?.id, i?.uuid],
              [action.payload.incidentId, action.payload.incidentUuid],
            )
          ) {
            i.incidentTickets = i.incidentTickets.filter(
              t =>
                !idArraysMatch(
                  [t?.id, t?.uuid],
                  [action.payload.ticketId, action.payload.ticketUuid],
                ),
            );
          }
          return i;
        }),
      };

    case ADD_TICKET_TO_INCIDENT:
      if (action.payload.incidentIndex !== undefined) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].incidentTickets.push(action.payload.ticket),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              idArraysMatch(
                [i?.id, i?.uuid],
                [action.payload.incidentId, action.payload.incidentUuid],
              )
            ) {
              let newIncident = Object.assign({}, i);
              newIncident.incidentTickets.push(action.payload.ticket);
              return newIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_INCIDENT_REVIEW:
      if (action.payload.property && action.payload.incidentIndex >= 0) {
        return void {
          incidents: (state.incidents[
            action.payload.incidentIndex
          ].incidentReview[action.payload.property] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i.incidentReview[action.payload.property] = action.payload.value;
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case ADD_NIC:
      if (action.payload.incidentIndex !== undefined) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].nicEntries.push(action.payload.nicEntry),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              let newIncident = Object.assign({}, i);
              newIncident.nicEntries.push(action.payload.nicEntry);
              return newIncident;
            } else {
              return i;
            }
          }),
        };
      }

    case REMOVE_NIC:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.nicEntryIndex !== undefined
      ) {
        return void {
          incidents: state.incidents[
            action.payload.incidentIndex
          ].nicEntries.splice(action.payload.nicEntryIndex, 1),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i.nicEntries = i.nicEntries.filter(
                entry =>
                  (!!entry.id && entry.id !== action.payload.nicEntryId) ||
                  (!!entry.uuid && entry.uuid !== action.payload.nicEntryUuid),
              );
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_NIC:
      if (
        action.payload.incidentIndex !== undefined &&
        action.payload.nicEntryIndex !== undefined
      ) {
        return void {
          incidents: (state.incidents[action.payload.incidentIndex].nicEntries[
            action.payload.nicEntryIndex
          ][action.payload.property] = action.payload.value),
        };
      } else {
        return void {
          incidents: state.incidents.map(i => {
            i.nicEntries.map(entry => {
              if (
                entry?.id === action.payload.nicEntryId ||
                entry?.uuid === action.payload.nicEntryId
              ) {
                entry[action.payload.property] = action.payload.value;
              }
              return entry;
            });
            return i;
          }),
        };
      }

    case UPDATE_QUERY_RETURNS:
      return void {
        incidents: state.incidents.map(i => {
          if (i?.id === action.payload.incidentId) {
            i.queryReturns = action.payload.queryReturns;
          }
          return i;
        }),
      };

    default:
      console.error("Unknown action type in incidentReducer: ", action);
      return state;
  }
};

export const personReducer = (state, action) => {
  switch (action.type) {
    case OPEN_PERSON: {
      let index = state.people.findIndex(i => i.id === action.payload.id);
      if (index > -1) {
        return state;
      } else {
        return { people: [...state.people, action.payload.person] };
      }
    }

    case CLOSE_PERSON:
      return { people: state.people.filter(i => i.id !== action.payload.id) };

    case SET_PERSON_INFO: {
      let result = action.payload?.[Object.keys(action.payload)[0]];
      if (!result) {
        return state;
      }
      let setPersonIndex = state.people.findIndex(i => i.id === result.id);
      if (setPersonIndex > -1) {
        return void {
          people: (state.people[setPersonIndex] = { ...result }),
        };
      } else {
        return void {
          people: state.people.push({ ...result }),
        };
      }
    }

    //updates a persons attributes: genericAttachments, groups, id, photos, uuid, validPersonAlerts
    case UPDATE_PERSON_ATTRIBUTE:
      return void state.people?.forEach(person => {
        if (!personMatchesPayload(person, action.payload)) return;
        person[action.payload.property] = action.payload.value;
      });

    //updates a person's personalAttributes
    case MERGE_PERSON_PERSONAL_ATTRIBUTE:
      return void state.people?.forEach(person => {
        if (!personMatchesPayload(person, action.payload)) return;
        person.personalAttribute = {
          ...person.personalAttribute,
          ...action.payload.value,
        };
      });
    case UPDATE_PERSON_PERSONAL_ATTRIBUTE:
      return void state.people?.forEach(person => {
        if (!personMatchesPayload(person, action.payload)) return;
        if (!person.personalAttribute) return;
        person.personalAttribute[action.payload.property] =
          action.payload.value;
      });

    default:
      console.error("Unknown action type in personReducer: ", action.type);
      return state;
  }
};

export const ticketReducer = (state, action) => {
  switch (action.type) {
    case OPEN_TICKET: {
      let index = state.tickets.findIndex(i => i.id === action.payload.id);
      if (index > -1) {
        return state;
      } else {
        return { tickets: [...state.tickets, action.payload.ticket] };
      }
    }

    case CLOSE_TICKET:
      return { tickets: state.tickets.filter(i => i.id !== action.payload.id) };

    case SET_TICKET_INFO: {
      let result = action.payload[Object.keys(action.payload)[0]];
      let setTicketIndex = state.tickets.findIndex(i => i.id === result.id);
      if (setTicketIndex > -1) {
        return void {
          tickets: (state.tickets[setTicketIndex] = { ...result }),
        };
      } else {
        return void {
          tickets: state.tickets.push({ ...result }),
        };
      }
    }

    case UPDATE_TICKET:
      return void {
        tickets: state.tickets.map(t => {
          if (idArraysMatch([t?.id, t?.uuid], [action.payload?.ticketId])) {
            if (action.payload.accessor) {
              t[action.payload.accessor][action.payload.property] =
                action.payload.value;
            } else {
              t[action.payload.property] = action.payload.value;
            }
          }
          return t;
        }),
      };

    default:
      console.error("Unknown action type in ticketReducer: ", action.type);
      return state;
  }
};

//slight misnomer - this will be a vis
const vehicleReducer = (state, action) => {
  switch (action.type) {
    case OPEN_VEHICLE: {
      let index = state.vehicles.findIndex(i => i.id === action.payload.id);
      if (index > -1) {
        return state;
      } else {
        return { vehicles: [...state.vehicles, action.payload.vehicle] };
      }
    }

    case CLOSE_VEHICLE:
      return {
        vehicles: state.vehicles.filter(i => i.id !== action.payload.id),
      };

    case SET_VEHICLE_INFO: {
      let result = action.payload[Object.keys(action.payload)[0]];
      let vehicleIndex = state.vehicles.findIndex(i => i.id === result.id);
      if (vehicleIndex > -1) {
        return void {
          vehicles: (state.vehicles[vehicleIndex] = { ...result }),
        };
      } else {
        return void {
          vehicles: state.vehicles.push({ ...result }),
        };
      }
    }

    default:
      console.error("Unknown action type in vehicleReducer: ", action.type);
      return state;
  }
};

const STATIC_TABS_LENGTH = 4;
const IncidentsContextProvider = props => {
  const { user } = useContext(UserContext);
  const [{ incidents }, incidentDispatch] = useImmerReducer(incidentReducer, {
    incidents: [],
    userId: user?.id,
  });
  const [{ people }, personDispatch] = useImmerReducer(personReducer, {
    people: [],
  });
  const [{ tickets }, ticketDispatch] = useImmerReducer(ticketReducer, {
    tickets: [],
  });
  const [{ vehicles }, vehicleDispatch] = useImmerReducer(vehicleReducer, {
    vehicles: [],
  });
  const [focusTab, setFocusTab] = useState(0);
  // const [incidentPanel, setIncidentPanel] = useState({})
  const [incidentPanel, setIncidentPanel] = useLocalStorage(
    `${user?.id}-incident-panels`,
    {},
  );
  const [prevLength, setPrevLength] = useState(incidents.length);

  useEffect(() => {
    //currentTab handles the instance where a refresh occurs when the user has the work queue, query returns, or logs tab open with incidents open.
    //This was causing issues with a tab being focused for only a single render before the incident pulled focus.
    const currentTab = localGetData(`${user?.id}-current-tab`);
    if (incidents.length > prevLength && (currentTab === 0 || currentTab > 3)) {
      setFocusTab(STATIC_TABS_LENGTH + incidents.length - 1);
    }
    setPrevLength(incidents.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents.length]);

  return (
    <IncidentsContext.Provider
      value={{
        incidents,
        incidentDispatch,
        people,
        personDispatch,
        tickets,
        ticketDispatch,
        vehicles,
        vehicleDispatch,
        focusTab,
        setFocusTab,
        incidentPanel,
        setIncidentPanel,
      }}
    >
      {props.children}
    </IncidentsContext.Provider>
  );
};

export default IncidentsContextProvider;
